/**
 * API Client med Axios
 * Konfigurerad med interceptors för auth tokens
 * Baserat på deque-web-main men anpassad för DQFleet
 * 
 * FÖRBÄTTRAD: Proaktiv token refresh för att undvika utloggning
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { useAuthStore } from '@/store/authStore';
import { SESSION_ACTIVE_WINDOW_MS } from '@/utils/sessionActivity';

// API ska alltid gå till SAMMA server som sidan när man inte är på localhost.
// Det undviker att prod-frontend (app.dqfleet.se) av misstag anropar test (188).
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') {
      return ''; // Same origin – alltid samma server som sidan (153 vid prod, 188 vid test)
    }
  }
  // I dev-läge: använd tom baseURL så att Vite-proxyn fångar /api-requests
  return import.meta.env.VITE_API_BASE_URL || '';
};
const API_BASE_URL = getApiBaseUrl();

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 sekunder
  withCredentials: true, // Se till att cookies skickas med för alla requests
});

let lastUserActivityAt = Date.now();
let refreshInFlight: Promise<string | null> | null = null;

export function noteSessionActivity(): void {
  lastUserActivityAt = Date.now();
}

/**
 * Dekoda JWT token för att kolla expiry
 * Returnerar expiry timestamp eller null om token är ogiltig
 */
const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : null; // Konvertera till millisekunder
  } catch {
    return null;
  }
};

/**
 * Kontrollera om token behöver förnyas proaktivt
 * Returnerar true om token går ut inom 2 minuter
 */
const shouldProactivelyRefresh = (token: string): boolean => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return false;
  
  const now = Date.now();
  const timeUntilExpiry = expiry - now;
  const twoMinutes = 2 * 60 * 1000; // 2 minuter i millisekunder
  
  return timeUntilExpiry < twoMinutes && timeUntilExpiry > 0;
};

/**
 * Request Interceptor
 * Lägger till access token i headers om den finns
 * FÖRBÄTTRAD: Proaktiv token refresh innan token går ut
 */
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    let token = Cookies.get('accessToken');

    // Proaktiv refresh bara när sidan själv kan läsa en JWT. httpOnly-session förnyas i bakgrunden.
    if (token && token.includes('.') && !config.url?.includes('/auth/refresh')) {
      if (shouldProactivelyRefresh(token)) {
        const newToken = await refreshSession();
        if (newToken) token = newToken;
      }
    }
    
    if (token && token.includes('.') && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // För FormData, låt axios sätta Content-Type automatiskt (inkluderar boundary)
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

/**
 * Refresh Access Token
 * Anropas när access token är utgången
 */
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const response = await api.post('/api/auth/refresh', {});
    const data = response.data?.data ?? {};

    if (data.session) {
      const currentState = useAuthStore.getState();
      if (currentState.user) {
        useAuthStore.setState({
          accessToken: 'cookie',
          refreshToken: 'cookie',
        });
      }
      return 'cookie';
    }

    if (typeof data.accessToken === 'string' && data.accessToken.includes('.')) {
      Cookies.set('accessToken', data.accessToken, { expires: 7 });
      if (typeof data.refreshToken === 'string') {
        Cookies.set('refreshToken', data.refreshToken, { expires: 30 });
      }
      const currentState = useAuthStore.getState();
      if (currentState.user) {
        useAuthStore.setState({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        });
      }
      return data.accessToken;
    }

    return null;
  } catch (error) {
    console.error('Refresh token failed:', error);
    return null;
  }
};

/** En förnyelse i taget. Parallella anrop delar resultatet i stället för att rotera token två gånger. */
export function refreshSession(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function redirectToLogin(): void {
  useAuthStore.getState().logOutUser();
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

/**
 * Response Interceptor
 * Hanterar 401 errors och försöker refresh token
 */
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _refreshRetry?: number };

    if (error.response?.status === 401 && originalRequest.url?.includes('/auth/refresh')) {
      const attempt = originalRequest._refreshRetry ?? 0;
      if (attempt < 2) {
        originalRequest._refreshRetry = attempt + 1;
        await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 300 : 1200));
        return api(originalRequest);
      }
      redirectToLogin();
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const newAccessToken = await refreshSession();
      if (newAccessToken && originalRequest.headers) {
        if (newAccessToken.includes('.')) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        } else {
          delete originalRequest.headers.Authorization;
        }
        return api(originalRequest);
      }
    }

    if (error.response?.status === 401) {
      redirectToLogin();
    }

    return Promise.reject(error);
  }
);

/**
 * Förnyar access-token var fjärde minut så länge användaren arbetar.
 * En stilla flik lämnas ifred så att inaktivitetstiden kan löpa ut.
 */
let backgroundRefreshInterval: ReturnType<typeof setInterval> | null = null;

const startBackgroundRefresh = () => {
  if (backgroundRefreshInterval) {
    clearInterval(backgroundRefreshInterval);
  }

  backgroundRefreshInterval = setInterval(async () => {
    const hasSession = Cookies.get('dqSession') === '1' || Boolean(Cookies.get('refreshToken'));
    const working = Date.now() - lastUserActivityAt < SESSION_ACTIVE_WINDOW_MS;
    if (hasSession && working) {
      await refreshSession();
    }
  }, 4 * 60 * 1000);
};

// Starta background refresh när modulen laddas
if (typeof window !== 'undefined') {
  // Vänta lite så att appen hinner ladda
  setTimeout(startBackgroundRefresh, 5000);
}

export default api;
export { getApiBaseUrl, startBackgroundRefresh };

